import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FlashMessagesService } from 'flash-messages-angular';
import { RouteHelper } from 'src/app/helpers';
import { Course, CourseModule, CourseModulePage, CourseModulePageType } from 'src/app/models';
import { AdminService } from 'src/app/services/admin.service';
import { CoursesService } from 'src/app/services/courses.service';
import { IEditCoursePageQueryParams } from './edit-course-page.viewmodel';

@Component({
  selector: 'app-edit-course-page',
  templateUrl: './edit-course-page.component.html',
  styleUrls: ['./edit-course-page.component.css']
})
export class EditCoursePageComponent implements OnInit {

  viewTitle!: string;
  currentPage!: CourseModulePage;
  routeParams: IEditCoursePageQueryParams = {} as any;

  // Initialize form, only using race in this
  form = this.fb.group({
    checkArray: this.fb.array([])
  });

  courseID: string = '';
  currentCourse!: Course;

  moduleID: string = '';
  currentModule!: CourseModule;

  // Page object
  pageID: string = '';
  page = {
    course:     '',
    module:     '',
    title:      '',
    type:       CourseModulePageType.Custom,
    content:    '',
    instance:   '',
  };

  isNew = false;
  submitting = false;

  pageTypes = Object.keys(CourseModulePageType);
  pageInstances = [] as string[];

  preBuiltPages: Indexable = {
    [CourseModulePageType.ActivityFlippingCards]: [
      'app-page-activity-flipping-cards-for-positive-communication-rw',
    ],
    [CourseModulePageType.ActivitySimpleQuiz]: [
      'app-page-activity-simple-quiz-problem-reporting-rw',
      'app-page-activity-simple-quiz-whos-responsible-rw',
      'app-page-activity-simple-quiz-moving-on-rw',
    ],
    [CourseModulePageType.ActivityBudgetingQuiz]: [
      'app-page-activity-budgeting-quiz-rw',
    ],
    [CourseModulePageType.ActivityDownloadReview]: [
      'app-page-activities-rw',
      'app-page-activity-compare-three-housing-units-rw',
      'app-page-sample-credit-report-order-rw',
      'app-page-sample-letter-about-incorrect-credit-report-rw',
      'app-page-activity-review-lease-agreement-and-answer-rw',
    ],
    [CourseModulePageType.ActivityEstimatingMonthlyIncome]: [
      'app-page-activity-estimating-monthly-income-nancy-rw',
    ],
    [CourseModulePageType.ActivityEstimatingMonthlyExpenses]: [
      'app-page-activity-estimating-monthly-expenses-nancy-rw',
    ],
    [CourseModulePageType.ActivityNeedsVsWants]: [
      'app-page-activity-needs-vs-wants-rw',
    ],
  }

  editorInitialized = false;
  hasUnsavedChanges = false;

  editorDefaultFontName = 'Roboto';
  editorDefaultFontFamily = `Roboto, "Helvetica Neue", sans-serif`;

  editorStyles = `
    body {
      font-family: ${this.editorDefaultFontFamily};
      font-size: 20px;
      font-weight: 300;
      line-height: 1.5;
    }

    a {
      cursor: pointer;
      text-decoration: none;
      color: #007bff;
      transition: all 0.2s ease-in-out;
    }

    a:hover {
      text-decoration: none;
      color: #0056b3;
      transition: all 0.2s ease-in-out;
    }

    iframe {
      max-width: 100%;
    }

    img {
      max-width: 100%;
      object-fit: cover;
      object-position: top left;
    }
  `;

  editorDefaultFonts = "Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats";

  editorFonts = `${this.editorDefaultFontName}=${this.editorDefaultFontFamily}; ${this.editorDefaultFonts}`;

  editorCodeExpanded = localStorage.getItem('expand_editor_html_code') === 'true';

  constructor(
    private coursesService: CoursesService,
    private adminService: AdminService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private flashMessage: FlashMessagesService
  ) {
    this.editorSetup = this.editorSetup.bind(this);
  }

  ngOnInit(): void {
    this.loadRouteParams();
    this.loadPage();
  }

  toggleSourceCode(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.editorCodeExpanded = !this.editorCodeExpanded;
    localStorage.setItem('expand_editor_html_code', String(this.editorCodeExpanded));
  }

  private loadRouteParams(): void {
    const queryParams = this.route.snapshot.queryParams as IEditCoursePageQueryParams;

    this.routeParams = RouteHelper.map(queryParams, {
      courseid: String,
      moduleid: String,
      pageid:   String,
    });

    this.courseID = this.routeParams.courseid || '';
    this.moduleID = this.routeParams.moduleid || '';
    this.pageID   = this.routeParams.pageid || '';

    this.currentCourse = this.coursesService.currentCourse;
    this.currentModule = this.currentCourse && this.moduleID ? this.currentCourse.modules.find(m => m.moduleID === this.moduleID) : null as any;
  }

  private loadPage() {
    const courseid = this.courseID;
    const pageid   = this.pageID;

    if (!this.currentCourse) {
      this.showError('Course not found.');
      this.router.navigate(['/admin/admin-courses']);
      return false;
    }

    if (!this.currentModule) {
      this.showError('Module not found.');
      this.router.navigate(['/admin/admin-courses'], { queryParams: { courseid } });
      return false;
    }

    this.isNew = pageid === 'new';

    if (this.isNew) {
      this.initNewPage();
    }
    else {
      this.initEditPage();
    }

    return true;
  }

  private initNewPage() {
    this.viewTitle = 'New Course Page';
    this.currentPage = null as any;
    this.hasUnsavedChanges = false;

    this.page.course = this.currentCourse.title;
    this.page.module = `${this.currentModule.moduleNumber}. ${this.currentModule.title}`;

    return true;
  }

  private initEditPage() {
    const courseid = this.routeParams.courseid;
    const moduleid = this.routeParams.moduleid;
    const pageid   = this.routeParams.pageid;

    if (!pageid) {
      this.router.navigate(['/admin/admin-courses'], { queryParams: { courseid, moduleid } });
      return false;
    }

    const found = this.currentModule.pages.find(page => page.pageID === pageid);

    if (!found) {
      this.showError('Page not found.');
      this.router.navigate(['/admin/admin-courses'], { queryParams: { courseid, moduleid } });
      return false;
    }

    this.viewTitle = 'Edit Course Page';
    this.currentPage = found;
    this.hasUnsavedChanges = false;

    this.page = JSON.parse(JSON.stringify(found)) as any;
    this.page.course = this.currentCourse.title;
    this.page.module = `${this.currentModule.moduleNumber}. ${this.currentModule.title}`;

    this.loadPageInstances(this.page.type);
    this.page.instance = this.extractPageInstanceFrom(this.page.content);

    return true;
  }

  editorSetup(editor: any) {
    editor.on('init', () => {
      this.editorInitialized = true;
      console.log('TinyMCE editor initialized.');
    });
  }

  goBack(force = false) {
    if (!force && this.hasUnsavedChanges && confirm('You have unsaved changes. Discard them and go back?') === false) {
      return;
    }

    const courseid = this.routeParams.courseid;
    const moduleid = this.routeParams.moduleid;
    this.router.navigate(['/admin/admin-courses'], { queryParams: { courseid, moduleid } });
  }

  onPageTypeSelected(value: string) {
    if (value !== CourseModulePageType.Custom) {
      this.pageInstances = this.loadPageInstances(value);
      this.page.instance = this.pageInstances.length === 1 ? this.pageInstances[0] : '';
    }
  }

  onBack() {
    this.goBack();
  }

  onContentChange(value: string) {
    const changed = this.editorInitialized ? this.page.content !== value : false;

    this.page.content = value;

    if (changed && !this.hasUnsavedChanges) {
      this.hasUnsavedChanges = true;
      // console.log('hasUnsavedChanges=true');
    }
  }

  async onSubmit() {
    if (this.submitting) {
      return;
    }

    this.submitting = true;

    try {
      if (this.isNew) {
        await this.create();
      }
      else {
        await this.update();
      }
    } finally {
      this.submitting = false;
    }
  }

  private async create() {
    // Create course page
    if (this.form.valid) {

      const courseid = this.routeParams.courseid || '';
      const moduleid = this.routeParams.moduleid || '';
      const data     = {
        title:   this.page.title,
        type:    this.page.type,
        content: this.page.type === CourseModulePageType.Custom ? this.page.content : `[${this.page.instance}]`
      };

      const res: any = await this.adminService.addCoursePage(courseid, moduleid, data).toPromise();

      // Successfully updated
      if (res.success) {

        // Display update success to user
        this.flashMessage.show(res.message || "Course page added.", {
          cssClass: 'alert-success',
          timeout: 5000
        });

        // Redirect back to pages list
        this.coursesService.courses = [];
        this.goBack(true);
      }
    }
  }

  private async update() {
    // Update course page
    if (this.form.valid) {

      const courseid = this.routeParams.courseid || '';
      const moduleid = this.routeParams.moduleid || '';
      const pageid   = this.routeParams.pageid || '';
      const data     = {
        title:   this.page.title,
        type:    this.page.type,
        content: this.page.type === CourseModulePageType.Custom ? this.page.content : `[${this.page.instance}]`
      };

      const res: any = await this.adminService.updateCoursePage(courseid, moduleid, pageid, data).toPromise();

      // Successfully updated
      if (res.success) {

        // Display update success to user
        this.flashMessage.show(res.message || "Course page updated.", {
          cssClass: 'alert-success',
          timeout: 5000
        });

        // Redirect back to pages list
        this.coursesService.courses = [];
        this.goBack(true);
      }
    }
  }

  private showError(message: string) {
    console.warn(message);
    this.flashMessage.show(message, {
      cssClass: 'alert-danger',
      timeout: 3000
    });
  }

  private loadPageInstances(type: string) {
    return this.pageInstances = this.preBuiltPages[type];
  }

  private extractPageInstanceFrom(content: string) {
    const shortcode = /\[(app-.+?)\]/;
    const match = content.match(shortcode);
    return match ? match[1] : '';
  }
}
